import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { updatePaymentRecord, getPaymentRecord, getTrustAccountBalance } from 'services/billingServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { ModalHeader } from 'shared/ModalHeader';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { useCaseAndLeadsList } from 'hooks/useCaseAndLeadList';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useListAccountsQuery } from 'hooks/queries/useListAccountsQuery';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { paymentStructure } from 'constants/dropdownOptions';
import { RecordPaymentEarnedAllocationFields } from 'modules/BillingV2/components/RecordPaymentEarnedAllocationFields/RecordPaymentEarnedAllocationFields';
import { isMappedViaCaseDefault } from 'modules/BillingV2/utils/depositAccountLockUtils';

const isFlatFeeStructure = (value) => value?.toLowerCase() === paymentStructure.FLAT_FEE.toLowerCase();

const getPaymentRecordSk = (selectedRecord) =>
  selectedRecord?.sk ?? (selectedRecord?.payment_record_id ? `payment_record#${selectedRecord.payment_record_id}` : undefined);

const toCents = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return null;
  }
  return Math.round(numericValue * 100);
};

const allocationAmountCents = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  return toCents(value) ?? 0;
};

export const EditPaymentRecordV2 = (props) => {
  const { isLeadSpecific = false } = props;

  const [typeOfPayment, setTypeOfPayment] = useState('offline');
  const [amount, setAmount] = useState();
  const [recordDate, setRecordDate] = useState();
  const [payFull, setPayFull] = useState(false);
  const [description, setDescription] = useState();
  let [formErrors, setFormErrors] = useState({
    contact: '',
    amount: '',
    record_date: '',
    payment_method: '',
    deposit_info: '',
  });
  const paymentMethods = [
    {
      label: 'Credit Card',
      value: 'Credit Card',
    },
    { label: 'Check', value: 'Check' },
    { label: 'Cash', value: 'Cash' },
    { label: 'Other', value: 'other' },
  ];
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();

  // Fetch accounts using React Query hook (no payload needed - fetches all accounts)
  // This will use cached data if the same query (no payload) is used elsewhere
  const { accountList: accountsList } = useListAccountsQuery({
    excludeFromLoading: true,
  });

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState();
  const [loading, setLoading] = useState(false);
  let [recordDetails, setRecordDetails] = useState();
  const { userContext } = useUserDetailsContext();
  const { addToast } = useToast();
  const [trustAccountBalance, setTrustAccountBalance] = useState();
  const [selectedTrustAccount, setSelectedTrustAccount] = useState();
  const [trustAccounts, setTrustAccounts] = useState([]);
  const [markAsEarnedChecked, setMarkAsEarnedChecked] = useState(false);
  const [transferToTrustAmount, setTransferToTrustAmount] = useState(null);
  const [temporaryFundAmount, setTemporaryFundAmount] = useState(null);
  const [markAsEarnedAmount, setMarkAsEarnedAmount] = useState(null);
  const [allocationTrustAccount, setAllocationTrustAccount] = useState();
  const [allocationErrors, setAllocationErrors] = useState({});
  const typeOfPayments = [
    {
      label: 'Offline',
      value: 'offline',
    },
    {
      label: 'From trust account',
      value: 'trust',
    },
  ];

  const { caseAndLeadsList } = useCaseAndLeadsList(props?.selectedRecord?.case_id);
  const { trustAccount, operatingAccount } = useMemo(() => {
    const trustId =
      caseAndLeadsList?.case_list?.[0]?.default_trust_account?.[0]?.account_id ||
      caseAndLeadsList?.lead_list?.[0]?.default_trust_account?.[0]?.account_id;
    const operatingId =
      caseAndLeadsList?.case_list?.[0]?.default_operating_account?.[0]?.account_id ||
      caseAndLeadsList?.lead_list?.[0]?.default_operating_account?.[0]?.account_id;

    const trustAccount = accountsList
      ?.filter((a) => a?.account_type === 'trust')
      ?.filter((account) => !trustId || account?.account_id === trustId);

    const operatingAccount = accountsList
      ?.filter((a) => a?.account_type === 'operating')
      ?.filter((account) => !operatingId || account?.account_id === operatingId);

    return {
      trustAccount: trustAccount || [],
      operatingAccount: operatingAccount || [],
    };
  }, [accountsList, caseAndLeadsList, typeOfPayment]);

  const allocationTrustAccounts = useMemo(() => accountsList?.filter((a) => a?.account_type === 'trust') || [], [accountsList]);

  const defaultTrustId = useMemo(
    () =>
      caseAndLeadsList?.case_list?.[0]?.default_trust_account?.[0]?.account_id ??
      caseAndLeadsList?.lead_list?.[0]?.default_trust_account?.[0]?.account_id ??
      null,
    [caseAndLeadsList]
  );

  const hasSplitMetadata = useMemo(() => recordDetails?.is_split === true && Boolean(recordDetails?.allocation), [recordDetails]);

  const isFlatFeeInvoiceEdit = useMemo(() => {
    const paymentFor = recordDetails?.payment_for ?? props.selectedRecord?.payment_for;
    const paymentRefNumber = recordDetails?.payment_ref_number ?? props.selectedRecord?.payment_ref_number;
    const structure =
      recordDetails?.payment_structure ?? recordDetails?.billing_preferences?.payment_structure ?? props.selectedRecord?.payment_structure;
    const isInvoice = paymentFor === 'invoice' || paymentRefNumber?.includes('INV');
    if (!isInvoice) {
      return false;
    }
    return isFlatFeeStructure(structure) || hasSplitMetadata;
  }, [recordDetails, props.selectedRecord, hasSplitMetadata]);

  const isFlatFeePaymentRequestEdit = useMemo(() => {
    const paymentFor = recordDetails?.payment_for ?? props.selectedRecord?.payment_for;
    const paymentRefNumber = recordDetails?.payment_ref_number ?? props.selectedRecord?.payment_ref_number;
    const structure =
      recordDetails?.payment_structure ?? recordDetails?.billing_preferences?.payment_structure ?? props.selectedRecord?.payment_structure;
    const isPaymentRequest = paymentFor === 'payment_request' || (paymentRefNumber && !paymentRefNumber.includes('INV'));
    if (!isPaymentRequest || isFlatFeeInvoiceEdit) {
      return false;
    }
    return isFlatFeeStructure(structure);
  }, [recordDetails, props.selectedRecord, isFlatFeeInvoiceEdit]);

  const showMarkAsEarned = useMemo(
    () =>
      hasSplitMetadata &&
      isFlatFeeInvoiceEdit &&
      typeOfPayment === 'offline' &&
      (selectedAccount?.account_type === 'operating' || recordDetails?.deposit_into === 'operating'),
    [hasSplitMetadata, isFlatFeeInvoiceEdit, typeOfPayment, selectedAccount, recordDetails?.deposit_into]
  );

  const editDepositAccountId = useMemo(
    () => selectedAccount?.account_id ?? recordDetails?.deposit_account_id ?? props.selectedRecord?.deposit_account_id ?? null,
    [selectedAccount?.account_id, recordDetails?.deposit_account_id, props.selectedRecord?.deposit_account_id]
  );

  const isDepositLocked = useMemo(
    () => hasSplitMetadata || isMappedViaCaseDefault(editDepositAccountId, null, caseAndLeadsList),
    [hasSplitMetadata, editDepositAccountId, caseAndLeadsList]
  );

  const showAllocationFields = showMarkAsEarned && !markAsEarnedChecked;

  useEffect(() => {
    if (!recordDetails || !hasSplitMetadata) {
      setMarkAsEarnedChecked(false);
      setTransferToTrustAmount(null);
      setTemporaryFundAmount(null);
      setMarkAsEarnedAmount(null);
      setAllocationTrustAccount(undefined);
      setAllocationErrors({});
      return;
    }

    const allocation = recordDetails.allocation ?? {};
    setMarkAsEarnedChecked(Boolean(recordDetails.mark_as_earned));
    setTransferToTrustAmount(allocation.trust_amount ?? null);
    setTemporaryFundAmount(allocation.temporary_funds_amount ?? null);
    setMarkAsEarnedAmount(allocation.earned_amount ?? null);
    if (allocation.trust_account_id) {
      const matchedTrust = allocationTrustAccounts.find((account) => account.account_id === allocation.trust_account_id);
      setAllocationTrustAccount(matchedTrust);
    } else {
      setAllocationTrustAccount(undefined);
    }
  }, [recordDetails, hasSplitMetadata, allocationTrustAccounts]);

  useEffect(() => {
    if (!showAllocationFields || allocationTrustAccount?.account_id) {
      return;
    }

    if (defaultTrustId) {
      const matchedDefaultTrust = allocationTrustAccounts.find((account) => account.account_id === defaultTrustId);
      if (matchedDefaultTrust) {
        setAllocationTrustAccount(matchedDefaultTrust);
        return;
      }
    }

    if (allocationTrustAccounts.length === 1) {
      setAllocationTrustAccount(allocationTrustAccounts[0]);
    }
  }, [showAllocationFields, defaultTrustId, allocationTrustAccounts, allocationTrustAccount?.account_id]);

  useEffect(() => {
    const paymentRecordSk = getPaymentRecordSk(props.selectedRecord);
    if (props.selectedRecord?.case_id && paymentRecordSk) {
      clearFormError();
      //loadTrustAccountBalance(props.selectedRecord?.billing_contact_id)
      getPaymentRecord(paymentRecordSk)
        .then((response) => {
          setRecordDetails({ ...response.data });
          let record_details = response.data;
          setAmount(record_details?.payment_record_amount);
          if (record_details.payment_record_date) setRecordDate(record_details.payment_record_date);
          if (record_details.payment_method) setSelectedPaymentMethod(record_details.payment_method);
          record_details.payment_record_status === 'FULL' ? setPayFull(true) : setPayFull(false);
          let payment_record_type = record_details.payment_record_type;
          payment_record_type === 'OFFLINE' ? setTypeOfPayment('offline') : setTypeOfPayment('trust');
          setDescription(record_details.description);
        })
        .catch((err) => {});
    }
  }, [props.selectedRecord]);

  useEffect(() => {
    if (selectedTrustAccount?.account_id) {
      loadTrustAccountBalance(selectedTrustAccount?.account_id, props?.selectedRecord?.case_id);
    }
  }, [selectedTrustAccount]);

  const onChangePayFull = (e) => {
    let due_amount = recordDetails?.due_amount;
    due_amount = due_amount ? parseFloat(due_amount) : 0;
    let payment_record_amount = recordDetails?.payment_record_amount;
    payment_record_amount = payment_record_amount ? parseFloat(payment_record_amount) : 0;
    let allowed_amount = due_amount + payment_record_amount;
    setPayFull(e.target.checked);
    if (e.target.checked) {
      setAmount(allowed_amount);
    }
  };

  useEffect(() => {
    if (accountsList && accountsList.length > 0) {
      let accountsToSet = [];
      if (typeOfPayment === 'offline') {
        if (trustAccount && operatingAccount) {
          accountsToSet = [...trustAccount, ...operatingAccount];
        } else {
          accountsToSet = [...accountsList];
        }
      } else {
        if (operatingAccount) {
          accountsToSet = [...operatingAccount];
        } else {
          let _accounts = accountsList.filter((index) => index.account_type === 'operating');
          accountsToSet = [..._accounts];
        }
        if (trustAccount) {
          setTrustAccounts([...trustAccount]);
        } else {
          let trust_accounts = accountsList.filter((index) => index.account_type === 'trust');
          setTrustAccounts([...trust_accounts]);
        }
      }
      setAccounts((previousAccounts) => {
        const previousIds = previousAccounts.map((account) => account.account_id).join(',');
        const nextIds = accountsToSet.map((account) => account.account_id).join(',');
        return previousIds === nextIds ? previousAccounts : accountsToSet;
      });

      // Find selected account from the accounts array that will be used in the dropdown
      let deposit_account_name = recordDetails?.deposit_account_name;
      let debit_account_name = recordDetails?.debit_account_name;
      let selected_account = accountsToSet.find((index) => index.account_name === deposit_account_name);
      let selected_trust_account = accountsList.find((index) => index.account_name === debit_account_name);
      if (selected_account && selected_account.account_id !== selectedAccount?.account_id) {
        setSelectedAccount(selected_account);
      }
      if (selected_trust_account && selected_trust_account.account_id !== selectedTrustAccount?.account_id) {
        setSelectedTrustAccount(selected_trust_account);
      }
    }
  }, [
    accountsList,
    typeOfPayment,
    recordDetails?.deposit_account_name,
    recordDetails?.debit_account_name,
    recordDetails?.payment_record_amount,
  ]);

  const loadTrustAccountBalance = (billing_contact_id, case_id) => {
    getTrustAccountBalance(billing_contact_id, case_id)
      .then((response) => {
        let available_balance = response?.data?.available_balance;
        available_balance = available_balance ? parseFloat(available_balance) : '';
        setTrustAccountBalance(available_balance);
      })
      .catch((err) => {});
  };

  const validateForm = () => {
    let due_amount = recordDetails?.due_amount;
    due_amount = due_amount ? parseFloat(due_amount) : 0;

    let payment_record_amount = recordDetails?.payment_record_amount;
    payment_record_amount = payment_record_amount ? parseFloat(payment_record_amount) : 0;

    let allowed_amount = due_amount + payment_record_amount;
    let isValid = true;
    if (!amount || amount <= 0) {
      isValid = false;
      formErrors.amount = 'Required a valid amount';
    }
    if (amount && !props?.selectedRecord?.fund_transfer_record && amount > allowed_amount) {
      isValid = false;
      formErrors.amount = "Amount can't be higher than the pending payment request amount";
    }
    if (!recordDate) {
      isValid = false;
      formErrors.record_date = 'Required';
    }
    if (!selectedAccount) {
      isValid = false;
      formErrors.deposit_info = 'Required';
    }
    if (typeOfPayment === 'offline' && !selectedPaymentMethod) {
      isValid = false;
      formErrors.payment_method = 'Required';
    }
    if (typeOfPayment === 'trust' && amount && amount > trustAccountBalance) {
      isValid = false;
      formErrors.amount = "Amount can't be higher than the trust account balance";
    }
    if (!validateAllocation()) {
      isValid = false;
    }
    setFormErrors({ ...formErrors });
    return isValid;
  };

  const validateAllocation = () => {
    if (!showMarkAsEarned) {
      setAllocationErrors({});
      return true;
    }

    if (markAsEarnedChecked) {
      setAllocationErrors({});
      return true;
    }

    const errors = {};
    const paymentAmountCents = toCents(amount);

    if (!allocationTrustAccount?.account_id) {
      errors.trust_account = 'Required';
    }

    const trustAmountCents = allocationAmountCents(transferToTrustAmount);
    const temporaryFundsCents = allocationAmountCents(temporaryFundAmount);
    const earnedAmountCents = allocationAmountCents(markAsEarnedAmount);

    if (transferToTrustAmount != null && transferToTrustAmount !== '' && trustAmountCents < 0) {
      errors.trust_amount = 'Must be zero or greater';
    }
    if (temporaryFundAmount != null && temporaryFundAmount !== '' && temporaryFundsCents < 0) {
      errors.temporary_funds_amount = 'Must be zero or greater';
    }
    if (markAsEarnedAmount != null && markAsEarnedAmount !== '' && earnedAmountCents < 0) {
      errors.earned_amount = 'Must be zero or greater';
    }

    if (Object.keys(errors).length > 0) {
      setAllocationErrors(errors);
      return false;
    }

    const allocationSumCents = trustAmountCents + temporaryFundsCents + earnedAmountCents;

    if (paymentAmountCents !== allocationSumCents) {
      setAllocationErrors({ allocation_sum: 'Allocation amounts must equal the payment amount' });
      return false;
    }

    setAllocationErrors({});
    return true;
  };

  const buildAllocationPayload = () => {
    if (!showMarkAsEarned) {
      return {};
    }

    if (markAsEarnedChecked) {
      return {
        mark_as_earned: true,
        allocation: {
          trust_amount: 0,
          trust_account_id: null,
          temporary_funds_amount: 0,
          earned_amount: Number(amount) || 0,
        },
        deposit_into: selectedAccount?.account_type ?? recordDetails?.deposit_into,
      };
    }

    return {
      mark_as_earned: false,
      allocation: {
        trust_amount: Number(transferToTrustAmount) || 0,
        trust_account_id: allocationTrustAccount?.account_id ?? null,
        temporary_funds_amount: Number(temporaryFundAmount) || 0,
        earned_amount: Number(markAsEarnedAmount) || 0,
      },
      deposit_into: selectedAccount?.account_type ?? recordDetails?.deposit_into,
    };
  };

  const onSubmit = () => {
    if (validateForm()) {
      if (!props.isEdit) {
        setLoading(true);
        let paymentRefId = props?.selectedRecord?.payment_ref_id;
        let data = {
          description: description,
          payment_record_amount: amount,
          payment_record_date: recordDate || '',
          payment_method: selectedPaymentMethod,
          payment_record_status: amount === props.selectedRecord.due_amount ? 'FULL' : 'PARTIAL',
          payment_record_type: typeOfPayment === 'offline' ? 'OFFLINE' : 'TRUST',
          old_payment_record_amount: props.selectedRecord?.payment_record_amount,
          payment_ref_id: paymentRefId,
        };
        data.deposit_account_id = selectedAccount?.account_id;
        data.deposit_account_name = selectedAccount?.account_name;
        data.deposit_into = selectedAccount?.account_type;
        if (typeOfPayment === 'trust') {
          data.debit_account_name = selectedTrustAccount?.account_name;
          data.debit_account_id = selectedTrustAccount?.account_id;
        }
        Object.assign(data, buildAllocationPayload());
        if (props?.selectedRecord?.payment_ref_number?.includes('INV')) {
          data.payment_for = 'invoice';
        }
        let requestData = { ...props.selectedRecord, ...data };
        updatePaymentRecord(requestData, isLeadSpecific ? isLeadSpecific : props?.selectedRecord?.is_lead)
          .then((response) => {
            setLoading(false);
            addToast(
              false,
              toastConstant.toasterType.SUCCESS,
              toastConstant.api.SUCCESS,
              toastConstant.message.UPDATE_PAYMENT_RECORD_SUCCESS
            );

            props.onSuccess();
          })
          .catch((err) => {
            console.log('err', err);
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.UPDATE_PAYMENT_RECORD_FAILED);
            setLoading(false);
          });
      }
    }
  };
  const clearFormError = () => {
    formErrors = {
      contact: '',
      amount: '',
      payment_method: '',
      deposit_info: '',
    };
    setFormErrors({ ...formErrors });
  };

  return (
    <>
      <GlobalLoader />
      <Dialog
        visible={props.show}
        onHide={props.onHide}
        // //header={"Record Payment"}
        header={ModalHeader}
        className="create-msg-popup"
        closable={false}
      >
        <div>
          {props.selectedRecord && (
            <>
              <div className="row">
                <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                  <div className="modal-heading F-size16 text-bold">{'Record Payment'}</div>
                </div>
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-12 col-sm-4 p-medium">Case Name</div>
                  <div className="col-12 col-sm-8">{props.selectedRecord.case_name}</div>
                </div>
                {!props?.selectedRecord?.fund_transfer_record && (
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-12 col-sm-4 p-medium">Due Amount</div>
                    <div className="col-12 col-sm-8">${recordDetails && recordDetails?.due_amount}</div>
                  </div>
                )}
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-12 col-sm-4 p-medium">Type of Payment</div>
                  <div className="col-12 col-sm-8">
                    <Dropdown
                      options={typeOfPayments}
                      value={typeOfPayment}
                      onChange={(e) => {
                        setTypeOfPayment(e.target.value);
                        formErrors.payment_method = '';
                        setFormErrors({ ...formErrors });
                        setSelectedPaymentMethod();
                      }}
                      filter
                      disabled={typeOfPayment && true}
                      //showClear={(typeOfPayment ==="" )? false : true}
                    />
                    {/* <span
                  className={
                    typeOfPayment === "offline"
                      ? "text-primary-dark text-decoration-none"
                      : ""
                  }
                  onClick={() => {
                    setTypeOfPayment("offline");
                  }}
                  role="button"
                >
                  Offline
                </span>
                <span
                  className={
                    typeOfPayment === "trust"
                      ? "text-primary-dark text-decoration-none ms-3"
                      : "ms-3"
                  }
                  onClick={() => {
                    setTypeOfPayment("trust");
                  }}
                  role="button"
                >
                  From trust account
                </span> */}
                  </div>
                </div>
                {!props?.selectedRecord?.fund_transfer_record && (
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-12 col-sm-4 p-medium">
                      Contact
                      <span className="text-danger">*</span>
                    </div>
                    <div className="col-12 col-sm-8">
                      {/* <Dropdown options={billingContacts} optionLabel={"name"} filterBy={"name"} value={billingContact}
                /> */}
                      <InputText className="input-shadow w-100" value={props.selectedRecord?.billing_contact_name} disabled={true} />
                    </div>
                  </div>
                )}
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-12 col-sm-4 p-medium">
                    Amount
                    <span className="text-danger">*</span>
                  </div>
                  <div className="col-12 col-sm-8">
                    <div className="d-flex align-items-center d-flex align-items-center text-nowrap">
                      <InputText
                        className="input-shadow w-50 border-0 p-2"
                        type="number"
                        value={amount}
                        onChange={(e) => {
                          setAmount(e.target.value);
                          formErrors.amount = '';
                          setFormErrors({ ...formErrors });
                        }}
                        disabled={payFull}
                        onWheel={(e) => e.target.blur()}
                      />
                      <div className="align-items-center ms-2 text-nowrap">
                        <label className="switch ms-2">
                          <input type="checkbox" checked={payFull} name="weekends" onChange={onChangePayFull} />{' '}
                          <span className="slider round"></span>
                        </label>
                        <span className="ms-2">Pay in full</span>
                      </div>
                    </div>

                    {formErrors && formErrors.amount && <span className="text-danger">{formErrors.amount}</span>}
                  </div>
                </div>
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-12 col-sm-4 p-medium">
                    Date
                    <span className="text-danger">*</span>
                  </div>
                  <div className="col-12 col-sm-8">
                    <TimezoneDatePicker
                      className="input-shadow w-100"
                      value={recordDate}
                      onChange={(value) => {
                        setRecordDate(value);
                        formErrors.record_date = '';
                        setFormErrors({ ...formErrors });
                      }}
                      timezone={userContext?.userDetails?.timezone}
                      isEndOfDay={true}
                    />
                    {formErrors && formErrors.record_date && <span className="text-danger">{formErrors.record_date}</span>}
                  </div>
                </div>
                {typeOfPayment === 'offline' && (
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-12 col-sm-4 p-medium">
                      Payment Method
                      <span className="text-danger">*</span>
                    </div>
                    <div className="col-12 col-sm-8">
                      <Dropdown
                        className="input-shadow w-100"
                        options={paymentMethods}
                        value={selectedPaymentMethod}
                        onChange={(e) => {
                          setSelectedPaymentMethod(e.value);
                          formErrors.payment_method = '';
                          setFormErrors({ ...formErrors });
                        }}
                        filter
                      />
                      {formErrors && formErrors.payment_method && <span className="text-danger">{formErrors.payment_method}</span>}
                    </div>
                  </div>
                )}
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-12 col-sm-4 p-medium">
                    Deposit Info
                    <span className="text-danger">*</span>
                  </div>
                  <div className="col-12 col-sm-8">
                    <Dropdown
                      className="input-shadow w-100"
                      options={accounts}
                      value={selectedAccount}
                      onChange={(e) => {
                        setSelectedAccount(e.value);
                        formErrors.deposit_info = '';
                        setFormErrors({ ...formErrors });
                      }}
                      onFilter={(e) => {
                        if (e?.value) {
                          setSelectedAccount(e.value);
                          formErrors.deposit_info = '';
                          setFormErrors({ ...formErrors });
                        } else {
                          formErrors.deposit_info = 'Required';
                          setFormErrors({ ...formErrors });
                          setSelectedAccount(null);
                        }
                      }}
                      optionLabel={'account_name'}
                      filter
                      disabled={isDepositLocked}
                      aria-label="Deposit Info"
                    />
                    {formErrors && formErrors.deposit_info && <span className="text-danger">{formErrors.deposit_info}</span>}
                  </div>
                </div>
                {typeOfPayment === 'trust' && (
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-12 col-sm-4 p-medium">
                      Trust
                      <span className="text-danger">*</span>
                    </div>
                    <div className="col-12 col-sm-8">
                      <Dropdown
                        className="input-shadow w-100"
                        options={trustAccounts}
                        value={selectedTrustAccount}
                        onChange={(e) => {
                          setSelectedTrustAccount(e.target.value);
                          formErrors.trust_account = '';
                          setFormErrors({ ...formErrors });
                        }}
                        optionLabel={'account_name'}
                        filter
                      />
                      {formErrors && formErrors.trust_account && <span className="text-danger">{formErrors.trust_account}</span>}
                    </div>
                  </div>
                )}
                <div className="d-flex align-items-start py-2 flex-wrap">
                  <div className="col-12 col-sm-4 p-medium d-flex align-items-center flex-wrap gap-1">
                    <span>Description</span>
                    <i
                      className="pi pi-info-circle text-muted cursor-pointer record-payment-description-tip flex-shrink-0 align-middle"
                      style={{ fontSize: '12px', lineHeight: 1, padding: '5px' }}
                      aria-label="More information"
                      role="img"
                    />
                  </div>
                  <div className="col-12 col-sm-8">
                    <TextSnippetWrapper
                      type="textarea"
                      className="input-shadow w-100 p-2"
                      value={description}
                      onChange={(e) => {
                        if (e.target.value.length <= 255) {
                          setDescription(e.target.value);
                        }
                      }}
                      maxLength={255}
                      autoComplete="off"
                    />
                    <small className="text-muted d-block text-end">{description?.length || 0}/255</small>
                  </div>
                </div>
                <Tooltip
                  target=".record-payment-description-tip"
                  content="This note will be visible to the client."
                  position="top"
                  showDelay={300}
                />
                {!isFlatFeePaymentRequestEdit && (
                  <RecordPaymentEarnedAllocationFields
                    showMarkAsEarned={showMarkAsEarned}
                    showAllocationFields={showAllocationFields}
                    markAsEarnedChecked={markAsEarnedChecked}
                    onMarkAsEarnedChange={(checked) => {
                      setMarkAsEarnedChecked(checked);
                      if (checked) {
                        setTransferToTrustAmount(null);
                        setTemporaryFundAmount(null);
                        setMarkAsEarnedAmount(null);
                        setAllocationTrustAccount(undefined);
                        setAllocationErrors({});
                      }
                    }}
                    allocationTrustAccounts={allocationTrustAccounts}
                    selectedAllocationTrustAccount={allocationTrustAccount}
                    onAllocationTrustChange={(value) => {
                      setAllocationTrustAccount(value);
                      setAllocationErrors((prev) => ({ ...prev, trust_account: undefined }));
                    }}
                    disableAllocationTrustSelection={Boolean(defaultTrustId)}
                    transferToTrustAmount={transferToTrustAmount}
                    onTransferToTrustAmountChange={(value) => {
                      setTransferToTrustAmount(value);
                      setAllocationErrors((prev) => ({ ...prev, trust_amount: undefined, allocation_sum: undefined }));
                    }}
                    temporaryFundAmount={temporaryFundAmount}
                    onTemporaryFundAmountChange={(value) => {
                      setTemporaryFundAmount(value);
                      setAllocationErrors((prev) => ({ ...prev, temporary_funds_amount: undefined, allocation_sum: undefined }));
                    }}
                    markAsEarnedAmount={markAsEarnedAmount}
                    onMarkAsEarnedAmountChange={(value) => {
                      setMarkAsEarnedAmount(value);
                      setAllocationErrors((prev) => ({ ...prev, earned_amount: undefined, allocation_sum: undefined }));
                    }}
                    allocationErrors={allocationErrors}
                  />
                )}
              </div>

              <div className="footer-btn">
                <div className="col-12 d-flex justify-content-end">
                  <Button className="p-button-secondary outline mx-2" label="Cancel" onClick={props.onHide}></Button>
                  <Button
                    className="p-button-secondary"
                    label={props.selectedExpense ? 'Update' : 'Save'}
                    onClick={onSubmit}
                    loading={loading}
                  ></Button>
                </div>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};
